import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getQuickBetTableColumns } from "utils/tableHelper/extraTableHelper";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";
import { deleteQuickBet, getQuickBets } from "services/quickBetService";

const useStyles = makeStyles(((theme: any) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  pickerContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "end",
    justifyContent: "space-between",
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
})) as any);

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.quickBet.edit?.replace(":id", data?._id));
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.QUICK_BET.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => actionHandlers?.handleDelete(data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const QuickBetList = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.QUICK_BET.UPDATE_ROLES
  );

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const createQuickBet = () => navigate(configData.routes.quickBet.create);

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      let payload = { pageNo, pageSize, sortDir: orderDirection };
      if (searchValue) {
        payload["skipPagination"] = true;
        payload["searchValue"] = searchValue;
      }
      const resp = await getQuickBets(payload);
      setListData(
        resp?.data?.items?.map((qb) => ({
          ...qb,
          betsText: JSON.stringify(qb.bets),
        }))
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const onDelete = async (id) => {
    try {
      const resp = await deleteQuickBet(id);
      showSnackbar(resp?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({
      itemName: `${data.currency} (territory: ${data.territory})`,
      id: data?._id,
    });
  };

  useEffect(() => {
    const initialColumns = getQuickBetTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Quick Bets"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a quick bet"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
            <CustomButton onClick={createQuickBet}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{ handleDelete: _openConsentDialog }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={() => onDelete(constentDetails.id)}
        />
      </Box>
    </ContentShell>
  );
};

export default QuickBetList;
